import type { AxiosError } from "axios";
import { apiClient } from "../../lib/http/apiClient";
import type { AuthUser } from "./types";

/**
 * `PUT /api/v1/profile` answers HTTP 428 instead of saving when the request
 * would change the number the user receives WhatsApp on. The payload is
 * parked server-side, so confirming with the code below applies the original
 * save — the caller never has to resend the form. This fires from both the
 * profile page and the onboarding wizards, since both save through the same
 * endpoint.
 */
export interface PhoneVerificationChallenge {
  field: string;
  pending_value: string;
  message: string;
}

export function toPhoneVerificationChallenge(err: unknown): PhoneVerificationChallenge | null {
  const ax = err as AxiosError<{ detail?: Record<string, unknown> }>;
  if (ax?.response?.status !== 428) return null;
  const detail = ax.response.data?.detail;
  if (!detail || detail.code !== "PHONE_VERIFICATION_REQUIRED" || typeof detail.field !== "string") return null;
  return {
    field: detail.field,
    pending_value: typeof detail.pending_value === "string" ? detail.pending_value : "",
    message:
      typeof detail.message === "string" && detail.message.trim()
        ? detail.message
        : "Enter the code sent to your WhatsApp to confirm this number.",
  };
}

/** Confirm a parked phone change and apply the save it was blocking. */
export async function verifyPhoneChange(field: string, otp: string): Promise<AuthUser> {
  const { data } = await apiClient.post<AuthUser>("/api/v1/profile/verify-phone-change", {
    field,
    otp,
  });
  return data;
}
